export interface EntityMetadata {
    entityType: string;
    category: 'person' | 'locality' | 'meta' | 'other' | 'blacklist';
    urlSection: 'Person' | 'localities' | 'meta' | 'other';
    urlPath: string;
    displayName: string;
    icon: string;
    color: string;
    confidence: 'high' | 'medium' | 'low';
    reason: string;
}

class EntityMetadataService {
    private static instance: EntityMetadataService;
    private resourceTypes: any[] = [];
    private metadataCache: Map<string, EntityMetadata> = new Map();

    private constructor() { }

    public static getInstance(): EntityMetadataService {
        if (!EntityMetadataService.instance) {
            EntityMetadataService.instance = new EntityMetadataService();
        }
        return EntityMetadataService.instance;
    }

    /**
     * Set resource types for the service to use
     */
    public setResourceTypes(resourceTypes: any[]): void {
        this.resourceTypes = resourceTypes;
        this.metadataCache.clear(); // Clear cache when resource types change
    }

    /**
     * Get metadata for an entity type
     */
    public getEntityMetadata(entityType: string): EntityMetadata {
        const cacheKey = entityType.toLowerCase();

        if (this.metadataCache.has(cacheKey)) {
            return this.metadataCache.get(cacheKey)!;
        }

        const metadata = this.categorizeEntity(entityType);
        this.metadataCache.set(cacheKey, metadata);
        return metadata;
    }

    /**
     * Get all entity metadata grouped by category
     */
    public getAllEntityMetadata(): Map<string, EntityMetadata[]> {
        const grouped = new Map<string, EntityMetadata[]>();

        this.resourceTypes.forEach((entityType: { name: string }) => {
            const metadata = this.getEntityMetadata(entityType.name);
            const category = metadata.category;

            if (!grouped.has(category)) {
                grouped.set(category, []);
            }
            grouped.get(category)!.push(metadata);
        });

        return grouped;
    }

    /**
     * Get entity metadata for a specific category
     */
    public getEntityMetadataByCategory(category: 'person' | 'locality' | 'meta' | 'other' | 'blacklist'): EntityMetadata[] {
        const allMetadata = this.getAllEntityMetadata();
        return allMetadata.get(category) || [];
    }

    /**
     * Centralized entity categorization logic
     */
    private categorizeEntity(entityType: string): EntityMetadata {
        const lowerEntityType = entityType.toLowerCase();

        // Find the entity type for this entity
        const entityTypeInfo = this.resourceTypes.find((type: { name: string }) =>
            type.name.toLowerCase() === lowerEntityType
        );

        if (!entityTypeInfo) {
            return this.createDefaultMetadata(entityType, 'other', 'Unknown entity type');
        }

        // Extract schema attributes for categorization (schema may be a string URN; attributes from extended schema lookup if needed)
        const schemaObj = typeof entityTypeInfo.schema === 'object' ? entityTypeInfo.schema : null;
        const schemaAttributes = schemaObj?.attributes || [];
        const attributeNames = schemaAttributes.map((attr: any) => attr.name?.toLowerCase() || '').filter(Boolean);

        // Key person indicators from schema attributes
        const keyPersonIndicators = [
            'userprincipalname', 'username', 'userName', 'emails', 'name',
            'displayName', 'active', 'externalId', 'id', 'meta', 'schemas'
        ];

        // Check for key person indicators
        const hasKeyPersonIndicators = keyPersonIndicators.some(indicator =>
            attributeNames.some((attr: string) => attr.includes(indicator.toLowerCase()))
        );

        // Check for person-related schema patterns (schema is URN string e.g. urn:dcps:scim:v1:Employee)
        const schemaStr = typeof entityTypeInfo.schema === 'string' ? entityTypeInfo.schema : '';
        const hasPersonSchema = schemaStr.includes('User') || schemaStr.includes('Person') ||
            entityTypeInfo.name.includes('User') || entityTypeInfo.name.includes('Person');

        // Check for locality indicators
        const localityIndicators = [
            'address', 'location', 'coordinates', 'latitude', 'longitude',
            'building', 'room', 'floor', 'campus', 'district'
        ];
        const hasLocalityIndicators = localityIndicators.some(indicator =>
            attributeNames.some((attr: string) => attr.includes(indicator.toLowerCase()))
        );

        // Check for meta indicators
        const metaIndicators = [
            'schema', 'attribute', 'definition', 'type', 'format'
        ];
        const hasMetaIndicators = metaIndicators.some(indicator =>
            attributeNames.some((attr: string) => attr.includes(indicator.toLowerCase()))
        );

        // Determine category based on schema analysis
        let category: 'person' | 'locality' | 'meta' | 'other' | 'blacklist';
        let confidence: 'high' | 'medium' | 'low';
        let reason: string;

        // Special override for top-level categories that are also entity types
        if (lowerEntityType === 'classes' || lowerEntityType === 'groups') {
            category = 'other';
            confidence = 'high';
            reason = 'Top-level category that is also an entity type';
        } else if (lowerEntityType === 'schemaentity' || lowerEntityType === 'schemaattribute') {
            category = 'meta';
            confidence = 'high';
            reason = 'Known meta entity type';
        } else if (lowerEntityType === 'photo' || lowerEntityType === 'image') {
            category = 'other';
            confidence = 'high';
            reason = 'Known other entity type';
        } else if (hasPersonSchema) {
            category = 'person';
            confidence = 'high';
            reason = 'Schema contains Person/User references';
        } else if (hasKeyPersonIndicators) {
            category = 'person';
            confidence = 'high';
            reason = 'Schema contains person-related attributes';
        } else if (hasLocalityIndicators) {
            category = 'locality';
            confidence = 'high';
            reason = 'Schema contains location-related attributes';
        } else if (hasMetaIndicators) {
            category = 'meta';
            confidence = 'high';
            reason = 'Schema contains meta-related attributes';
        } else {
            // Fallback to pattern matching on entity type name
            if (lowerEntityType.includes('person') || lowerEntityType.includes('user') ||
                lowerEntityType.includes('employee') || lowerEntityType.includes('student') ||
                lowerEntityType.includes('contractor') || lowerEntityType.includes('applicant')) {
                category = 'person';
                confidence = 'medium';
                reason = 'Entity type name suggests person type';
            } else if (lowerEntityType.includes('school') || lowerEntityType.includes('building') ||
                lowerEntityType.includes('class') || lowerEntityType.includes('center')) {
                category = 'locality';
                confidence = 'medium';
                reason = 'Entity type name suggests locality type';
            } else {
                category = 'other';
                confidence = 'low';
                reason = 'No clear indicators found';
            }
        }

        // Determine URL section and path
        let urlSection: 'Person' | 'localities' | 'meta' | 'other';
        let urlPath: string;

        switch (category) {
            case 'person':
                urlSection = 'Person';
                urlPath = `Person/${lowerEntityType}`;
                break;
            case 'locality':
                urlSection = 'localities';
                urlPath = `localities/${lowerEntityType}`;
                break;
            case 'meta':
                urlSection = 'meta';
                urlPath = `meta/${lowerEntityType}`;
                break;
            default:
                urlSection = 'other';
                urlPath = `other/${lowerEntityType}`;
        }

        return {
            entityType,
            category,
            urlSection,
            urlPath,
            displayName: this.getDisplayName(entityType),
            icon: this.getIcon(entityType),
            color: this.getColor(entityType),
            confidence,
            reason
        };
    }

    /**
     * Create default metadata for unknown entity types
     */
    private createDefaultMetadata(entityType: string, category: 'person' | 'locality' | 'meta' | 'other' | 'blacklist', reason: string): EntityMetadata {
        return {
            entityType,
            category,
            urlSection: category === 'person' ? 'Person' : 'localities',
            urlPath: category === 'person' ? `Person/${entityType.toLowerCase()}` : `localities/${entityType.toLowerCase()}`,
            displayName: this.getDisplayName(entityType),
            icon: this.getIcon(entityType),
            color: this.getColor(entityType),
            confidence: 'low',
            reason
        };
    }

    /**
     * Get display name for entity type
     */
    private getDisplayName(entityType: string): string {
        // Simple pluralization logic - can be enhanced
        const lower = entityType.toLowerCase();
        if (lower.endsWith('y')) {
            return entityType.slice(0, -1) + 'ies';
        } else if (lower.endsWith('s') || lower.endsWith('sh') || lower.endsWith('ch')) {
            return entityType + 'es';
        } else {
            return entityType + 's';
        }
    }

    /**
     * Get icon for entity type
     */
    private getIcon(entityType: string): string {
        const lower = entityType.toLowerCase();
        switch (lower) {
            case 'student':
            case 'employee':
            case 'contractor':
            case 'applicant':
                return 'Person';
            case 'school':
            case 'building':
            case 'class':
                return 'LocationOn';
            case 'group':
                return 'Group';
            default:
                return 'Person';
        }
    }

    /**
     * Get color for entity type
     */
    private getColor(entityType: string): string {
        const lower = entityType.toLowerCase();
        switch (lower) {
            case 'student':
                return '#1976d2';
            case 'contractor':
                return '#f57c00';
            case 'employee':
                return '#388e3c';
            case 'school':
                return '#7b1fa2';
            case 'building':
                return '#5d4037';
            default:
                return '#757575';
        }
    }
}

export default EntityMetadataService;
